import React from "react";
import { Box, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import messages from "./messages";
import { tokens } from "../../../assets/theme";
import { useState } from "react";
import { format } from 'date-fns';
import { CircularProgress } from "@mui/material";

//Controls
import { ResponsiveLine } from '@nivo/line'
let showCycleTimeLabel = false;

const CustomTooltip = ({ point }) => (
    <div
        style={{
            background: '#333333',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            color: '#ffffff',
        }}
    >
        {format(new Date(point.data.x), 'yyyy-MM-dd HH:mm')}
        <br />
        { showCycleTimeLabel ? <FormattedMessage {...messages.cycleTime} /> : <FormattedMessage {...messages.pace} /> }: {point.data.yFormatted}
    </div>
);

const Diagram = (props) => {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [attachment, setAttachment] = useState([]);
    const { formatMessage } = useIntl();
    const nrOfDataPoints = props.machinePaceDetailsQuery.data?.length ?? 0;
    showCycleTimeLabel = props.showCycleTime;

    const setXAxisResolution = () => {
        return nrOfDataPoints < 20 ? "every 1 minute" : 
        nrOfDataPoints < 100 ? "every 5 minutes" :
        nrOfDataPoints < 250 ? "every 15 minutes" :
        nrOfDataPoints < 500 ? "every 20 minutes" :
        nrOfDataPoints < 1000 ? "every 30 minutes" :
        "every 60 minutes";
    }

    const MyResponsiveLine = ({ data, values }) => (
        <ResponsiveLine
            data={data}
            tooltip={CustomTooltip}
            theme={{
                grid: {
                    line: {
                        stroke: '#333333', 
                        strokeWidth: 1,
                    },
                },
                axis: {
                    ticks: {
                        text: {
                            fill: '#cccccc', 
                        },
                    },
                    legend: {
                        text: {
                            fill: '#cccccc', 
                        },
                    },

                },
                legends: {
                    text: {
                        fill: '#cccccc', 
                    },
                },
            }}

            colors={['#ffffff', 'transparent', 'transparent', 'transparent', 'transparent', 'transparent', 'transparent']} 
            colorBy="id" // Ensure colors are applied by series id
            margin={{ top: 30, right: 50, bottom: 100, left: 80 }}
            xScale={{
                type: "time",
                format: "%Y-%m-%d %H:%M:%S",
                precision: "minute",
            }}
            yScale={{
                type: 'linear',
                min: 'auto',
                max: 'auto',
                stacked: false,
                reverse: false
            }}
            yFormat=" >-.2f"
            axisTop={null}
            axisRight={null}
            axisBottom={{
                format: "%Y-%m-%d %H:%M",
                formatType: "time",
                tickValues: setXAxisResolution(),
                legendOffset: 70,
                legendPosition: "middle",
                tickRotation: 25,
            }}
            axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: props.showCycleTime ? formatMessage(messages.cycleTime) : formatMessage(messages.pace),
                legendOffset: -60,
                legendPosition: 'middle',
                truncateTickAt: 0
            }}
            lineWidth={1}
            enablePoints={false}
            enableGridX={false}
            enableGridY={true}
            pointSize={10}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabel="data.yFormatted"
            pointLabelYOffset={-12}
            enableArea={true} 
            areaOpacity={0.6}
            areaBlendMode="normal"
            enableTouchCrosshair={true}
            useMesh={true}
            areaBaselineValue={props.showCycleTime ? props.idealCycleTime : props.idealPace}
            defs={[
                {
                    id: 'area-top',
                    type: 'linearGradient',
                    colors: [
                        { offset: 20, color: props.showCycleTime ? 'rgba(193, 0, 0, 0)' : 'rgba(0, 193, 0, 0)' },
                        { offset: 100, color: props.showCycleTime ? 'rgba(193, 0, 0, 1)' : 'rgba(0, 193, 0, 1)' },
                    ],
                },
                {
                    id: 'area-bottom',
                    type: 'linearGradient',
                    colors: [
                        { offset: 0, color: props.showCycleTime ? 'rgba(0, 193, 0, 1)' : 'rgba(193, 0, 0, 1)' },
                        { offset: 90, color: props.showCycleTime ? 'rgba(0, 193, 0, 0)' : 'rgba(193, 0, 0, 0)' },
                    ],
                },
                {
                    id: 'area-off',
                    type: 'linearGradient',
                    colors: [
                        { offset: 50, color: 'rgba(0, 193, 0, 0)' },
                        { offset: 50, color: 'rgba(0, 193, 0, 0)' },
                    ],
                },
                {
                    id: 'solid-fill', 
                    type: 'patternDots', 
                    background: 'blue', 
                    color: 'blue', 
                    size: 10, 
                    padding: 0, 
                    stagger: false, 
                },
            ]}
            fill={[
                {
                    match: {
                        id: 'Max', 
                    },
                    id: 'area-top',
                },
                {
                    match: {
                        id: 'Min', 
                    },
                    id: 'area-bottom',
                },

                { match: '*', id: 'area-off' }
            ]}
        />
    )

    if (props.machinePaceDetailsQuery.isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px', width: '75%' }}>
                <CircularProgress color="secondary" />
            </Box>)
    }

    let maxPace = 0;
    let minPace = 0;
    let minTimestamp = null;
    let maxTimestamp = null;

    minTimestamp = formatTS(props.machinePaceDetailsQuery.data[0].timestamp);
    maxTimestamp = formatTS(props.machinePaceDetailsQuery.data[props.machinePaceDetailsQuery.data.length-1].timestamp);

    let values = props.machinePaceDetailsQuery.data?.map((item) => {
        maxPace = Math.max(maxPace, props.showCycleTime ? item.cycleTime : item.pace);
        minPace = Math.min(minPace, props.showCycleTime ? item.cycleTime : item.pace);
        return { 'x': formatTS(item.timestamp), 'y': props.showCycleTime ? item.cycleTime : item.pace };
    })

    function formatTS(ts) {
        if(ts === null){ return null; }
        let date = new Date(ts);
        return formatDate(date) + " " + formatFullTime(date) ;
    }

    function formatDate(date) {
        let locale = 'sv-SE';
        return date ? new Date(date).toLocaleDateString(locale) : "";
    }
    function formatFullTime(date) {
        let locale = 'sv-SE';
        return date ? new Date(date).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : "";
    }

    function generateData(idealPace, avgPace, minPace, maxPace, minTimestamp, maxTimestamp, values) {

        let idealLine = getLine("Ideal", minTimestamp, maxTimestamp, idealPace);
        let avgLine = getLine("Avg", minTimestamp, maxTimestamp, avgPace);
        let minLine = getLine("Min", minTimestamp, maxTimestamp, minPace);
        let topPace = idealPace * 1.5;
        if (idealPace * 1.5 < maxPace) {
            topPace = maxPace + maxPace / 10;
        }
        let topLine = getLine("Max", minTimestamp, maxTimestamp, topPace);

        let tempData = [
            {
                "id": "Data",
                "data": values
            }
        ];

        tempData.push(idealLine);
        tempData.push(avgLine);
        tempData.push(minLine);
        tempData.push(topLine);

        return tempData;

    }
    let data = props.showCycleTime ? generateData(props.idealCycleTime, props.avgCycleTime, minPace, maxPace, minTimestamp, maxTimestamp, values) : generateData(props.idealPace, props.avgPace, minPace, maxPace, minTimestamp, maxTimestamp, values);

    function getLine(id, minTimestamp, maxTimestamp, value) {
        return {
            "id": id,
            "data": [
                {
                    "x": minTimestamp,
                    "y": value
                },
                {
                    "x": maxTimestamp,
                    "y": value
                }
            ]
        }
    }

    return (
        <>
            <Box height="500px" width="100%" sx={{ background: colors.primary[400] }} marginTop="20px" padding="5px">
                <Box sx={{ backgroundColor: colors.primary[500] }} width="100%" height="100%">

                    <MyResponsiveLine data={data} values={values} />
                </Box>
            </Box>
        </>
    )
};

export default Diagram;